import { Button, EmptyState, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
/**
 * Every published page, whether anything has happened since it went live, and
 * the report that says whether it meant anything.
 *
 * The reports were a screen of their own, and the honest one and therefore the
 * empty one: a page appears in a report only once it has been live fourteen
 * days and the move has cleared the significance floor, which most pages never
 * do. That refusal is the product, and it left a real question unanswered — did
 * this page do *anything* — so the two screens answered halves of one question
 * about one page. One row per page now, the conclusion in its column and the
 * report under the row.
 *
 * The two counts are counts and are printed as counts. Whether the difference
 * between them means anything is what the report says; a page with no report
 * yet says how many days are left instead of showing an arrow.
 */

import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, useState } from "react";
import { api, type PageStanding, type ReflowRow } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { agent, article as articlePath, brand } from "~/core/paths";
import { keys, read } from "~/core/query";
import { Async } from "~/core/ui/states";
import { Drill, DrillLink } from "~/modules/analytics/components/detail";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { matches, SortHeader, TableTools, useSort } from "~/modules/analytics/components/table";
import { contentPath } from "~/modules/analytics/lib/loop";

/**
 * The wait before a page is measured, as `loops::publish::REFLOW_AFTER_DAYS`
 * sets it. A workflow may ask for a longer one (`observation_days`), but that
 * is an input of the workflow's step and nothing the panel is sent, so the
 * countdown uses the default rather than inventing a figure.
 */
const REFLOW_DAYS = 14;

type Column = "title" | "live" | "before" | "since";

export function PublicationsPage({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const { locale } = useI18n();
	const [query, setQuery] = useState("");
	const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
	const sort = useSort<Column>("live", "asc");

	const state = useQuery({
		queryKey: [...keys.entity(entityId), "reflow-pages", { range: filters.range, locale: filters.locale }],
		queryFn: () => api.reflowPages(entityId, filters),
		...read,
	});
	const reports = useQuery({
		queryKey: [...keys.entity(entityId), "reflows", { range: filters.range, locale: filters.locale }],
		queryFn: () => api.reflows(entityId, filters),
		...read,
	});

	// Newest first from the server, and every report of a page is kept: a page
	// rewritten after its first report has been measured twice.
	const byPage = new Map<string, ReflowRow[]>();
	for (const report of reports.data ?? []) {
		const list = byPage.get(report.page_id);
		if (list) list.push(report);
		else byPage.set(report.page_id, [report]);
	}

	function toggle(id: string) {
		setOpen((previous) => {
			const next = new Set(previous);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}

	return (
		<StageShell
			entityId={entityId}
			reading="publications"
			lede={t("reflowPages.lede", { days: REFLOW_DAYS })}
			filters={filters}
			setFilters={setFilters}
		>
			<Async
				state={{ ...state, data: state.data?.pages }}
				empty={
					<EmptyState
						size="compact"
						title={t("reflowPages.empty")}
						description={t("reflowPages.emptyBody")}
						action={<DrillLink to={contentPath(brand(entityId), "published")}>{t("empty.goPublished")}</DrillLink>}
					/>
				}
			>
				{(all) => {
					const rows = sort.apply(
						all.filter((row) => matches(query, row.title, row.kind)),
						(row, column) =>
							column === "live"
								? row.days_live
								: column === "before"
									? row.cited_before
									: column === "since"
										? row.cited_since
										: row.title,
					);

					return (
						<>
							<TableTools
								sort={sort.config([
									{ id: "title", label: t("table.page") },
									{ id: "live", label: t("reflowPages.live") },
									{ id: "before", label: t("reflowPages.before") },
									{ id: "since", label: t("reflowPages.since") },
								])}
								query={query}
								onQuery={setQuery}
								placeholder={t("table.title")}
								shown={rows.length}
								total={all.length}
								csv={{ name: "published-pages", rows: csv(all) }}
							/>

							{rows.length === 0 ? (
								<EmptyState size="compact" title={t("table.noMatch")} />
							) : (
								<div className="table-scroll">
									<Table variant="resource">
										<TableHeader>
											<TableRow>
												<SortHeader className="wrap" label={t("table.page")} column="title" sort={sort} />
												<SortHeader className="num" label={t("reflowPages.live")} column="live" sort={sort} />
												<SortHeader className="num" label={t("reflowPages.before")} column="before" sort={sort} />
												<SortHeader className="num" label={t("reflowPages.since")} column="since" sort={sort} />
												<TableHead>{t("reflowPages.conclusion")}</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{rows.map((row) => (
												<Fragment key={row.page_id}>
													<TableRow>
														<TableCell className="wrap">
															<Button
																variant="ghost"
																size="sm"
																aria-expanded={open.has(row.page_id)}
																aria-controls={`report-${row.page_id}`}
																aria-label={`${t("reflowPages.report")}: ${row.title}`}
																onClick={() => toggle(row.page_id)}
															>
																{open.has(row.page_id) ? <ChevronDown /> : <ChevronRight />}
															</Button>
															<DrillLink to={articlePath(entityId, row.page_id)}>{row.title}</DrillLink>
															<div className="faint">
																{row.kind} · {fmt.date(locale, row.published_at)}
															</div>
														</TableCell>
														<TableCell className="num mono">{t("reflowPages.days", { n: row.days_live })}</TableCell>
														<TableCell className="num mono">{row.cited_before}</TableCell>
														<TableCell className="num mono">
															{/* Both counts are of this page's citations, so the
															    one worth opening is the one since it went live. */}
															<Drill view={{ kind: "citations", label: row.title, filters: { page: row.page_id } }}>
																{row.cited_since}
															</Drill>
														</TableCell>
														<TableCell>
															<Conclusion row={row} />
														</TableCell>
													</TableRow>
													{open.has(row.page_id) ? (
														<TableRow id={`report-${row.page_id}`}>
															<TableCell colSpan={5}>
																<Report
																	row={row}
																	reports={byPage.get(row.page_id) ?? []}
																	entityId={entityId}
																	workflowId={state.data?.weekly_workflow_id ?? null}
																/>
															</TableCell>
														</TableRow>
													) : null}
												</Fragment>
											))}
										</TableBody>
									</Table>
								</div>
							)}
						</>
					);
				}}
			</Async>
		</StageShell>
	);
}

/**
 * What the report says about this page, or why it says nothing yet.
 *
 * Three states and not two: measured and significant, measured and not, and
 * not yet measured. Collapsing the last two would tell somebody a page did
 * nothing when the truth is that nobody has looked.
 *
 * Rows the server marked insignificant say "no conclusion" and show no arrow,
 * even though the delta is right there in the data. Reporting it anyway is the
 * single easiest way to make this product dishonest, and it would work — for
 * about one quarter, until a customer checked.
 */
function Conclusion({ row }: { row: PageStanding }) {
	const t = useT();
	const { locale } = useI18n();

	if (row.reflow_id === null) {
		const left = Math.max(0, REFLOW_DAYS - row.days_live);
		return <span className="faint">{left > 0 ? t("reflowPages.waiting", { n: left }) : t("reflowPages.due")}</span>;
	}
	if (!row.significant || row.delta === null) return <span className="pill">{t("reflow.notSignificant")}</span>;
	return <Delta delta={row.delta} locale={locale} />;
}

function Delta({ delta, locale }: { delta: number; locale: Parameters<typeof fmt.signed>[0] }) {
	return (
		<span className={delta >= 0 ? "pill pill-success" : "pill pill-danger"}>
			{delta >= 0 ? "▲" : "▼"} {fmt.signed(locale, delta)}
		</span>
	);
}

/**
 * The report under a page's row: the same questions asked in the week before
 * it went live and again after, and the share of answers that cited it.
 *
 * Where there is none yet, what writes one. The comparison is a step of the
 * brand's Weekly Optimization workflow, so "measurement due" is a question
 * about that workflow's next run, and the way to it is the Agent.
 */
function Report({
	row,
	reports,
	entityId,
	workflowId,
}: {
	row: PageStanding;
	reports: ReflowRow[];
	entityId: string;
	workflowId: string | null;
}) {
	const t = useT();
	const { locale } = useI18n();

	// The reports are listed over the period on screen, and a page's latest
	// one can be older than that.
	if (reports.length === 0 && row.reflow_id !== null) return <p className="hint">{t("reflowPages.outside")}</p>;
	if (reports.length === 0) {
		const left = Math.max(0, REFLOW_DAYS - row.days_live);
		return (
			<p className="hint">
				{left > 0 ? t("reflowPages.waitingBody", { n: left }) : t("reflowPages.dueBody")}{" "}
				<DrillLink to={agent(entityId, workflowId ? `w/${workflowId}` : "home")}>
					{t(workflowId ? "reflowPages.openWeekly" : "reflowPages.openWorkflow")}
				</DrillLink>
			</p>
		);
	}

	return (
		<Table variant="resource">
			<TableHeader>
				<TableRow>
					<TableHead>{t("reflowPages.weeks")}</TableHead>
					<TableHead className="num">{t("reflow.before")}</TableHead>
					<TableHead className="num">{t("reflow.after")}</TableHead>
					<TableHead className="num">{t("reflow.delta")}</TableHead>
					<TableHead>{t("table.date")}</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{reports.map((report) => (
					<TableRow key={report.id}>
						<TableCell className="mono">
							{report.before_week} → {report.after_week}
						</TableCell>
						<TableCell className="num mono">
							{report.before_share === null ? "—" : fmt.percent(locale, report.before_share)}
						</TableCell>
						<TableCell className="num mono">
							{/* The after figure opens the citations of this page: the
							    report is the claim, and they are what it is made of. */}
							<Drill view={{ kind: "citations", label: report.page_title, filters: { page: report.page_id } }}>
								{report.after_share === null ? "—" : fmt.percent(locale, report.after_share)}
							</Drill>
						</TableCell>
						<TableCell className="num">
							{report.significant && report.delta !== null ? (
								<Delta delta={report.delta} locale={locale} />
							) : (
								<span className="pill">{t("reflow.notSignificant")}</span>
							)}
						</TableCell>
						<TableCell className="mono">{fmt.date(locale, report.created_at)}</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	);
}

function csv(rows: PageStanding[]): (string | number)[][] {
	return [
		["title", "kind", "published_at", "days_live", "cited_before", "cited_since", "significant", "delta"],
		...rows.map((row) => [
			row.title,
			row.kind,
			row.published_at,
			row.days_live,
			row.cited_before,
			row.cited_since,
			row.reflow_id === null ? "" : String(row.significant),
			row.delta === null ? "" : row.delta.toFixed(2),
		]),
	];
}
